import React, { useState, useMemo } from 'react';
import ToolShell, { ACCENT, INK, fmt, roundTo, Field, input, card } from '../tools/ToolShell';

/*
 * PUBLIC, no-login lead-magnet tool: /tools/youtube-money-calculator
 * Targets the high-volume India query "how much does YouTube pay" — a creator
 * enters monthly views + niche and sees an estimated AdSense earnings range
 * (₹ + $) plus what brand deals on top could add. Funnels to CreatorKhata.
 *
 * Built entirely from <ToolShell> — this file is ONLY the tool's config + math
 * + the two cards. Everything else (header, hero, SEO, CTA, footer) is shared.
 */

// Estimated YouTube AdSense RPM (₹ paid out per 1,000 *monetised* views) by
// niche, India 2025 benchmarks. Finance/tech command far higher CPMs than
// entertainment. RPM = revenue after YouTube's 45% cut, so these are payout figures.
const NICHE_RPM = {
  'Finance / Business': 180,
  'Tech / Gadgets': 140,
  'Education': 95,
  'Beauty / Fashion': 70,
  'Fitness / Health': 65,
  'Food / Cooking': 55,
  'Travel': 60,
  'Lifestyle / Vlog': 45,
  'Gaming': 40,
  'Comedy / Entertainment': 35,
  'News / Politics': 30,
  'Other': 55,
};
const NICHES = Object.keys(NICHE_RPM);
const USD = 83; // ₹ per $ (indicative)

// Share of total views that are actually monetised (skippable-ad served + not
// blocked). Long-form ~0.55; Shorts monetise far lower via the Shorts pool.
const MONETISED_SHARE = { 'Long-form videos': 0.55, 'Mostly Shorts': 0.12 };
const FORMATS = Object.keys(MONETISED_SHARE);

export default function YtMoneyToolPage() {
  const [niche, setNiche] = useState(NICHES[0]);
  const [format, setFormat] = useState(FORMATS[0]);
  const [views, setViews] = useState('');

  const result = useMemo(() => {
    const v = parseInt(views, 10) || 0;
    if (!v) return null;
    const rpm = NICHE_RPM[niche] || 55;
    const monetised = v * MONETISED_SHARE[format];
    const mid = (monetised / 1000) * rpm;
    if (mid <= 0) return null;
    const step = mid >= 20000 ? 1000 : 500;
    const low = roundTo(mid * 0.7, step);
    const high = roundTo(mid * 1.3, step);
    // Brand deals typically dwarf AdSense: ~1–2 integrations/mo at ~₹1/view of an avg video.
    const dealLow = roundTo(v * 0.04, step);   // conservative monthly sponsor add-on
    const dealHigh = roundTo(v * 0.12, step);
    return { low, high, dealLow, dealHigh, yearLow: low * 12, yearHigh: high * 12, rpm };
  }, [views, niche, format]);

  return (
    <ToolShell
      seo={{
        title: 'YouTube Money Calculator India 2025 — Estimate Your Earnings — CreatorKhata',
        description: 'Free YouTube earnings calculator for Indian creators. Estimate your monthly AdSense income in ₹ from your views and niche, plus what brand deals could add on top.',
        path: '/tools/youtube-money-calculator',
      }}
      h1="YouTube Money Calculator (India)"
      sub="Wondering how much YouTube pays in India? Enter your monthly views and niche to estimate your AdSense earnings — plus what brand deals could add. No signup."
      cta={{
        heading: 'AdSense is the small half. Brand deals are the big one.',
        body: 'Most creators earn far more from sponsorships than from ads — if they get paid on time. CreatorKhata runs your brand-deal pitches, contracts, GST invoices and payment tracking in one place.',
      }}
    >
      {/* Inputs */}
      <div style={card()}>
        <Field label="Niche">
          <select style={input} value={niche} onChange={e => setNiche(e.target.value)}>
            {NICHES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>
        <Field label="Content format">
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {FORMATS.map(f => (
              <button key={f} onClick={() => setFormat(f)} style={{
                flex: 1, padding: '0.55rem', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
                border: `1.5px solid ${format === f ? ACCENT : '#e0e0e0'}`,
                background: format === f ? ACCENT : 'white', color: format === f ? 'white' : '#666',
              }}>{f}</button>
            ))}
          </div>
        </Field>
        <Field label="Total monthly views">
          <input style={input} type="number" value={views} onChange={e => setViews(e.target.value)} placeholder="e.g. 500000" />
        </Field>
        <p style={{ fontSize: '0.72rem', color: '#aaa', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
          Estimates use 2025 India RPM benchmarks by niche, adjusted for how much of your views actually get monetised
          ({format === 'Mostly Shorts' ? 'Shorts pay far less than long-form' : 'long-form serves more ads'}). Real payouts vary with watch time, audience geography & ad demand.
        </p>
      </div>

      {/* Output */}
      <div style={card()}>
        {!result ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem 1rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>▶️</div>
            Enter your <b style={{ color: ACCENT }}>monthly views</b> on the left — your estimated earnings appear here <b>instantly</b>. No submit button.
          </div>
        ) : (
          <div>
            <div style={{ borderBottom: `3px solid ${ACCENT}`, paddingBottom: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ color: '#888', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Estimated AdSense / month</div>
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.9rem', color: ACCENT }}>
                {fmt(result.low)} – {fmt(result.high)}
              </div>
              <div style={{ color: '#999', fontSize: '0.8rem' }}>
                ≈ ${Math.round(result.low / USD).toLocaleString('en-IN')} – ${Math.round(result.high / USD).toLocaleString('en-IN')} • ~₹{result.rpm} RPM
              </div>
            </div>

            <Stat label="Per year (AdSense)" value={`${fmt(result.yearLow)} – ${fmt(result.yearHigh)}`} />
            <Stat label="Brand deals could add / month" value={`${fmt(result.dealLow)} – ${fmt(result.dealHigh)}`} accent />

            <div style={{ marginTop: '0.9rem', background: '#f1f8f4', border: `1px solid ${ACCENT}22`, borderRadius: 10, padding: '0.85rem 1rem', fontSize: '0.82rem', color: '#2a5d42' }}>
              💡 For most creators, <b>brand deals out-earn AdSense several times over</b>. The hard part isn't landing them — it's pricing, invoicing and actually getting paid.
            </div>

            <p style={{ fontSize: '0.72rem', color: '#aaa', marginTop: '0.75rem' }}>
              Indicative estimate (2025 India benchmarks). Actual AdSense depends on watch time, audience country, seasonality and ad rates.
            </p>
          </div>
        )}
      </div>
    </ToolShell>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.6rem 0', borderBottom: '1px solid #eee' }}>
      <span style={{ fontSize: '0.85rem', color: '#555', fontWeight: 600 }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: '0.95rem', color: accent ? ACCENT : INK }}>{value}</span>
    </div>
  );
}
